import React from "react";
import { ReactQuestionFactory } from "survey-react-ui";
import { QuestionNonValue, Serializer, ElementFactory } from "survey-core";

export class SampleStoryQuestionModel extends QuestionNonValue {
  static type = "samplestory";

  constructor(name: string) {
    super(name);
    this.createItemValues("successBullets");
  }

  getType() {
    return SampleStoryQuestionModel.type;
  }

  get exampleCondition(): string {
    return this.getPropertyValue("exampleCondition", "");
  }

  set exampleCondition(val: string) {
    this.setPropertyValue("exampleCondition", val);
  }

  get exampleMedication(): string {
    return this.getPropertyValue("exampleMedication", "");
  }

  set exampleMedication(val: string) {
    this.setPropertyValue("exampleMedication", val);
  }

  get exampleDenialReason(): string {
    return this.getPropertyValue("exampleDenialReason", "");
  }

  set exampleDenialReason(val: string) {
    this.setPropertyValue("exampleDenialReason", val);
  }

  get content(): string {
    return this.getPropertyValue("content", "");
  }

  set content(val: string) {
    this.setPropertyValue("content", val);
  }

  get successBullets(): string[] {
    return this.getPropertyValue("successBullets", []);
  }

  set successBullets(val: string[]) {
    this.setPropertyValue("successBullets", val);
  }

  get successBulletsList(): string[] {
    const bullets = this.successBullets || [];
    if (!Array.isArray(bullets)) {
      return [];
    }

    return bullets
      .map((item) => {
        if (typeof item === "string") {
          return item;
        }

        if (item && typeof item === "object") {
          return (
            (item as { text?: string; value?: string }).text ||
            (item as { text?: string; value?: string }).value ||
            ""
          );
        }

        return "";
      })
      .filter(Boolean);
  }

  public get name(): string {
    return this.getPropertyValue("name");
  }

  public set name(val: string) {
    this.setPropertyValue("name", val);
  }
}

export type SampleStoryQuestionComponentProps = {
  question: SampleStoryQuestionModel;
};

export function SampleStoryQuestionComponent({
  question,
}: SampleStoryQuestionComponentProps) {
  if (!question.content) {
    return null;
  }

  return (
    <div
      className={question.cssClasses?.root}
      style={{
        backgroundColor: "#fef3c7",
        border: "1px solid #fbbf24",
        borderRadius: "4px",
        padding: "10px",
        marginBottom: "10px",
      }}
    >
      <div style={{ marginBottom: "6px" }}>
        <strong style={{ fontSize: "14px" }}>📖 Sample Success Story</strong>
      </div>
      {question.exampleCondition && (
        <div style={{ marginBottom: "4px", fontSize: "13px" }}>
          <strong>Condition:</strong> {question.exampleCondition}
        </div>
      )}
      {question.exampleMedication && (
        <div style={{ marginBottom: "4px", fontSize: "13px" }}>
          <strong>Medication:</strong> {question.exampleMedication}
        </div>
      )}
      {question.exampleDenialReason && (
        <div style={{ marginBottom: "6px", fontSize: "13px" }}>
          <strong>Denial Reason:</strong> {question.exampleDenialReason}
        </div>
      )}
      <div
        style={{
          marginBottom: "6px",
          fontSize: "13px",
          lineHeight: "1.3",
          whiteSpace: "pre-wrap",
        }}
      >
        {question.content}
      </div>
      {question.successBulletsList.length > 0 && (
        <div>
          <strong
            style={{ fontSize: "13px", display: "block", marginBottom: "4px" }}
          >
            Why It Worked:
          </strong>
          <ul style={{ margin: "0", paddingLeft: "16px", lineHeight: "1.3" }}>
            {question.successBulletsList.map((bullet, index) => (
              <li key={index} style={{ fontSize: "13px", marginBottom: "2px" }}>
                {bullet}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

export function registerSampleStoryQuestion() {
  ElementFactory.Instance.registerElement(
    "samplestory",
    (name: string) => new SampleStoryQuestionModel(name),
  );

  Serializer.addClass(
    "samplestory",
    [
      {
        name: "exampleCondition",
        category: "general",
        displayName: "Example Condition",
        type: "string",
        default: "",
        isSerializable: true,
        visibleIndex: 0,
      },
      {
        name: "exampleMedication",
        category: "general",
        displayName: "Example Medication",
        type: "string",
        default: "",
        isSerializable: true,
        visibleIndex: 1,
      },
      {
        name: "exampleDenialReason",
        category: "general",
        displayName: "Example Denial Reason",
        type: "string",
        default: "",
        isSerializable: true,
        visibleIndex: 2,
      },
      {
        name: "content",
        category: "general",
        displayName: "Story Content",
        type: "text",
        isRequired: true,
        default: "",
        isSerializable: true,
        visibleIndex: 3,
      },
      {
        name: "successBullets",
        category: "general",
        displayName: "Success Bullets",
        type: "itemvalue[]",
        isRequired: false,
        default: [],
        isSerializable: true,
        visibleIndex: 4,
      },
    ],
    () => new SampleStoryQuestionModel(""),
    "nonvalue",
  );

  ReactQuestionFactory.Instance.registerQuestion(
    SampleStoryQuestionModel.type,
    (propsOrName: string | SampleStoryQuestionComponentProps) => {
      const props =
        typeof propsOrName === "string"
          ? { question: new SampleStoryQuestionModel(propsOrName) }
          : propsOrName;
      return React.createElement(SampleStoryQuestionComponent, props);
    },
  );
}
